import { onUnmounted, ref, toRef, watch, type MaybeRefOrGetter } from 'vue';
import {
  mediaStatusEventsService,
  type MediaStatusEventDto,
} from '@/api/media-status-events.service';
import type { ServerSentEventService } from '@/api/server-sent-event.service';

export function useMediaStatusEvents(enabled: MaybeRefOrGetter<boolean> = true) {
  const events = ref<MediaStatusEventDto[]>([]);
  const error = ref<Error | null>(null);
  const isConnected = ref(false);

  let subscription: { unsubscribe: () => void } | null = null;
  let service: ServerSentEventService<MediaStatusEventDto> | null = null;

  const disconnect = () => {
    subscription?.unsubscribe();
    subscription = null;
    service?.close();
    service = null;
    isConnected.value = false;
  };

  const connect = () => {
    if (subscription || service) return;

    service = mediaStatusEventsService();
    subscription = service.open().subscribe({
      next: (event) => {
        if (event) {
          events.value.push(event);
        }
      },
      error: (err) => {
        error.value = err;
        isConnected.value = false;
      },
      complete: () => {
        isConnected.value = false;
      },
    });
    isConnected.value = true;
  };

  watch(
    toRef(enabled),
    (isEnabled) => {
      if (isEnabled) {
        connect();
      } else {
        disconnect();
      }
    },
    { immediate: true },
  );

  onUnmounted(disconnect);

  return {
    events,
    error,
    isConnected,
  };
}
